import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import capitalize from 'lodash-es/capitalize';
import { map, Observable, of, withLatestFrom } from 'rxjs';

import { ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { compareAscBy } from '@blaze-shared/platform-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  BlazeTableSchema,
  DatasetColumn,
  DEFAULT_ID_COL,
  getColumnKey,
  isNonObjectValueSchemaColumns,
  PageContextSchema,
  removeInternalNonIdColumns,
} from '@blaze-shared/schema-domain';
import { isDeepEqual, regexpGetStoredVariableName, SelectOption } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { normalizeVarName } from '@blaze-shared/variables-util';
import { getAssignNodesVars, MAIN_INPUT_NAME, Workflow } from '@blaze-shared/workflow-domain';

import { selectContext } from './+state/intellisense.selectors';
import { IntelliQueryService } from './intelli-query.service';
import {
  AggregateFunctionOption,
  EvalOption,
  IntelliContext,
  QueryOperatorOption,
} from './models/intellisense';

export interface ElementOptionsConfig {
  detailsSchema?: BlazeSchema;
  formListSchema?: BlazeSchema;
  formSchema?: BlazeSchema;
  inputDetailsSchema?: Record<string, BlazeSchema | undefined>;
  inputFormSchema?: Record<string, BlazeSchema | undefined>;
  inputSchemas?: Record<string, BlazeSchema | undefined>;
  pageContextSchema?: PageContextSchema | null;
  skipRuntimeVars?: (('account' | 'page' | 'user' | 'vars') | string)[];
  wizardSchema?: BlazeSchema;
}

@Injectable({ providedIn: 'root' })
export class IntelliDropdownOptionsService {
  private readonly iQueryService = inject(IntelliQueryService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<any>);
  private readonly variablesFacade = inject(VariablesFacade);

  getAssignedVarsOptions(workflow: Workflow): SelectOption<string, string>[] {
    return getAssignNodesVars(workflow.nodes).map<SelectOption<string, string>>((outputName) => ({
      label: outputName?.replace(/^@@/, '') ?? '',
      value: outputName,
    }));
  }

  getOptionsForAggregateFunctions(
    config: { includeNone?: boolean } = { includeNone: false },
  ): Observable<AggregateFunctionOption[]> {
    const options: AggregateFunctionOption[] = [
      { name: 'ARRAY', functionType: 'ARRAY' },
      { name: 'AVERAGE', functionType: 'AVG' },
      { name: 'COUNT ROWS', functionType: 'COUNT' },
      { name: 'MAX', functionType: 'MAX' },
      { name: 'MEDIAN', functionType: 'MEDIAN' },
      { name: 'MIN', functionType: 'MIN' },
      { name: 'SUM', functionType: 'SUM' },
    ];
    if (config?.includeNone) {
      options.unshift({ name: 'NONE', functionType: 'NONE' });
    }
    return of(options);
  }

  getOptionsForColumnsInDataset(
    datasetId: string | undefined,
    schemaOrder?: boolean,
  ): Observable<DatasetColumn[]> {
    if (!datasetId) {
      return of([]);
    }

    return this.schemaService.get(datasetId).pipe(
      map((schema) => {
        if (schema?.columns) {
          const schemaColumns = schema?.columns?.length ? [...schema.columns] : [];
          return schemaOrder ? schemaColumns : schemaColumns.toSorted(compareAscBy('name'));
        }
        return [];
      }),
    );
  }

  getOptionsForColumnsInSchema(schema?: BlazeSchema, sorted = true): Observable<DatasetColumn[]> {
    if (schema?.columns) {
      const schemaColumns = schema?.columns?.length ? [...schema.columns] : [];
      return sorted ? of(schemaColumns.toSorted(compareAscBy('name'))) : of(schemaColumns);
    }
    return of([]);
  }

  /**
   * Creates from context object, both options for a dropdown, and associated columns
   * (which can be used with intelisense).
   */
  getOptionsAndColumnsForElement(
    optionsConfig: ElementOptionsConfig,
  ): Observable<{ options: EvalOption[]; columns: DatasetColumn[] }> {
    const {
      detailsSchema,
      formSchema,
      formListSchema,
      inputDetailsSchema,
      inputFormSchema,
      inputSchemas,
      pageContextSchema,
      skipRuntimeVars,
      wizardSchema,
    } = optionsConfig;

    return this.store.select(selectContext).pipe(
      withLatestFrom(
        this.schemaService.obtainAccountsSchema(),
        this.schemaService.obtainUsersSchema(),
      ),
      map(([intelliContext, accountsSchema, userSchema]) => {
        const columns: DatasetColumn[] = [];
        const options: EvalOption[] = [];

        // Form schema
        const form = this.createFormSchemaOptions(formSchema);
        columns.push(...form.columns);
        options.push(...form.options);

        // Form List item schema
        const formList = this.createFormListSchemaOptions(formListSchema);
        columns.push(...formList.columns);
        options.push(...formList.options);

        // Details schema
        const details = this.createDetailsSchemaOptions(detailsSchema, formSchema?.id);
        columns.push(...details.columns);
        options.push(...details.options);

        // Wizard schema
        const wizard = this.createWizardSchemaOptions(wizardSchema);
        columns.push(...wizard.columns);
        options.push(...wizard.options);

        // Inputs schemas
        const inputsSchemas = [inputSchemas, inputFormSchema, inputDetailsSchema].filter((v) => v);
        const inputs = this.createInputSchemasOptions(
          inputsSchemas,
          detailsSchema?.id,
          formSchema?.id,
          skipRuntimeVars,
        );
        columns.push(...inputs.columns);
        options.push(...inputs.options);

        // Context columns
        const contextVars = this.createContextVarsOptions(
          intelliContext,
          accountsSchema,
          userSchema,
          pageContextSchema,
          skipRuntimeVars,
        );
        columns.push(...contextVars.columns);
        options.push(...contextVars.options);

        return this.disambiguateDuplicateOptions(columns, options);
      }),
    );
  }

  getOptionsForQueryOperators(column?: DatasetColumn | null): Observable<QueryOperatorOption[]> {
    const operators = this.iQueryService.getOperatorsForDataType(column);
    const options = operators.map((op) => ({
      value: op,
      label: this.iQueryService.getComparisonOperatorName(op, column),
    }));
    return of(options);
  }

  private createContextVarsOptions(
    intelliContext: IntelliContext,
    accountsSchema: BlazeTableSchema | null,
    userSchema: BlazeTableSchema | null,
    pageContextSchema?: PageContextSchema | null | undefined,
    skipRuntimeVars?: (('account' | 'page' | 'user' | 'vars') | string)[],
  ): { columns: DatasetColumn[]; options: EvalOption[] } {
    const options: EvalOption[] = [];
    const columns: DatasetColumn[] = [];

    let runtimeVarName = '';
    let runtimeVarLabel = '';
    let name = '';

    const { storedVarsColumns, storedVarsOptions } = this.createStoredVariablesOptions();
    columns.push(...storedVarsColumns);
    options.push(...storedVarsOptions);

    for (const contextVarName in intelliContext) {
      if (skipRuntimeVars && skipRuntimeVars.indexOf(contextVarName) > -1) {
        continue;
      }
      switch (contextVarName) {
        case 'account':
          name = 'Current Account';
          runtimeVarName = '$$Account?.';
          runtimeVarLabel = 'Current Account | ';
          break;
        case 'page':
          name = 'Page';
          runtimeVarName = '$$Page?.';
          runtimeVarLabel = 'Page | ';
          break;
        case 'user':
          name = 'Current User';
          runtimeVarName = '$$User?.';
          runtimeVarLabel = 'Current User | ';
          break;
        case 'varUrl':
          name = 'URL Parameter';
          runtimeVarName = '$$Url?.';
          runtimeVarLabel = 'URL Parameter | ';
          break;
        default:
          name = '';
          runtimeVarName = '$$' + contextVarName;
          runtimeVarLabel = '';
      }
      const contextVar =
        Object.prototype.hasOwnProperty.call(intelliContext, contextVarName) &&
        intelliContext[contextVarName];

      if (
        contextVar &&
        Object.keys(contextVar)?.length &&
        contextVarName !== 'page' &&
        contextVarName !== 'account' &&
        contextVarName !== 'user' &&
        contextVarName !== 'varUrl'
      ) {
        for (const contextVarKey in contextVar) {
          if (
            Object.prototype.hasOwnProperty.call(contextVar, contextVarKey) &&
            contextVarKey !== DEFAULT_ID_COL
          ) {
            // Displayed option
            const label = `${runtimeVarLabel}${contextVarKey}`;
            const value =
              contextVarName === 'vars'
                ? normalizeVarName(contextVarKey)
                : `${runtimeVarName}["${contextVarKey}"]`;
            options.push({ label, name, value });

            // Column associated with the displayed option

            const contextVarValue: any = contextVar[contextVarKey];
            const dataType = capitalize(typeof contextVarValue) as ColumnDataType;
            const column: DatasetColumn = {
              columnType: dataType,
              dataType,
              isProfilePicture: false,
              name: label,
            };
            columns.push(column);
          }
        }
      }
      // Page Context
      else if (contextVarName === 'page' && contextVar?.['columns']) {
        const pageContextColumns: DatasetColumn[] = removeInternalNonIdColumns(
          pageContextSchema?.columns ?? [],
        );
        for (const pageContextColumn of pageContextColumns) {
          const label = `${runtimeVarLabel}${pageContextColumn.name}`;
          const value = `${runtimeVarName}?.["${getColumnKey(pageContextColumn)}"]`;
          const column: DatasetColumn = {
            columnType: pageContextColumn.columnType,
            dataType: pageContextColumn.dataType,
            id: pageContextColumn.id,
            isProfilePicture: false,
            name: label,
          };
          if (!pageContextColumn.isId && !pageContextColumn.isInternal) {
            options.push({ label, name, value });
            columns.push(column);
          }
        }
      }
      // Current Account
      else if (contextVarName === 'account') {
        for (const contextVarKey in contextVar) {
          const label = `${runtimeVarLabel}${contextVarKey}`;
          const value = `${runtimeVarName}["${contextVarKey}"]`;
          const column = accountsSchema?.columns.find((c) => c.name === contextVarKey);
          if (column) {
            options.push({ label, name, value });
            columns.push({ ...column, name: label });
          }
        }
      }
      // Current User
      else if (contextVarName === 'user') {
        const addedColumns = new Set<string>();
        const userSchemaColumns = userSchema?.columns
          ? removeInternalNonIdColumns(userSchema.columns)
          : [];
        // Add all columns from User schema
        userSchemaColumns.forEach((column) => {
          const label = `${runtimeVarLabel}${column.name}`;
          const value = `${runtimeVarName}["${column.name}"]`;
          options.push({ label, name, value });
          columns.push({ ...column, name: label });
          addedColumns.add(label);
        });
        // Add remaining User columns which may not be present in the schema (or schema was not found)
        // TODO(michal): this may currently cause deleted columns to show up in intellisense. This logic can be
        // removed and the issue fixed once User table is present in all projects. Until then, we need to keep it
        // because we aren't sure whether the column no longer exists or whether table no longer exists. Similar
        // issue occurs for root and admin users which aren't explicitly part of the project.
        if (!userSchemaColumns.length) {
          for (const contextVarKey in contextVar) {
            const label = `${runtimeVarLabel}${contextVarKey}`;
            const value = `${runtimeVarName}["${contextVarKey}"]`;
            if (!addedColumns.has(label)) {
              const contextVarValue: any = contextVar[contextVarKey];
              const dataType = capitalize(typeof contextVarValue) as ColumnDataType;
              const column: DatasetColumn = {
                columnType: dataType,
                dataType,
                isProfilePicture: false,
                name: label,
              };
              options.push({ label, name, value });
              columns.push(column);
              addedColumns.add(label);
            }
          }
        }
      }
      // Current Url Parameters
      else if (contextVarName === 'varUrl') {
        for (const contextVarItem of contextVar) {
          const label = `${runtimeVarLabel}${contextVarItem.name}`;
          const value = `${runtimeVarName}["${contextVarItem.name}"]`;
          options.push({ label, name, value });
          columns.push(<DatasetColumn>{
            name: label,
            columnType: 'String',
            dataType: 'String',
          });
        }
      }
    }

    return { columns, options };
  }

  private createDetailsSchemaOptions(
    detailsSchema: BlazeSchema | undefined,
    formSchemaId?: string,
  ): { columns: DatasetColumn[]; options: EvalOption[] } {
    const options: EvalOption[] = [];
    if (!detailsSchema?.columns?.length || detailsSchema.id === formSchemaId) {
      return { columns: [], options: [] };
    }

    const columns = removeInternalNonIdColumns(detailsSchema.columns);
    for (const column of columns) {
      options.push({
        label: column.name,
        name: detailsSchema?.name ?? 'details',
        value: `$$details["${getColumnKey(column)}"]`,
      });
    }

    return { columns, options };
  }

  private createFormSchemaOptions(formSchema: BlazeSchema | undefined): {
    columns: DatasetColumn[];
    options: EvalOption[];
  } {
    const options: EvalOption[] = [];
    let columns = formSchema?.columns ?? [];
    if (!formSchema?.columns?.length) {
      return { columns: [], options: [] };
    }

    columns = removeInternalNonIdColumns(columns);
    const formColumns = columns.filter((column) => !column.isInternal);
    for (const column of formColumns) {
      options.push({
        label: column.name,
        name: formSchema?.name ?? 'form',
        value: `$$form["${getColumnKey(column)}"]`,
      });
    }

    return { columns, options };
  }

  private createFormListSchemaOptions(formSchema: BlazeSchema | undefined): {
    columns: DatasetColumn[];
    options: EvalOption[];
  } {
    const options: EvalOption[] = [];
    let columns = formSchema?.columns ?? [];
    if (!formSchema?.columns?.length) {
      return { columns: [], options: [] };
    }

    columns = removeInternalNonIdColumns(columns);
    const formColumns = columns.filter((column) => !column.isInternal);
    for (const column of formColumns) {
      options.push({
        label: column.name,
        name: formSchema?.name ?? 'form',
        value: `$$formList["${getColumnKey(column)}"]`,
      });
    }

    return { columns, options };
  }

  private createInputSchemasOptions(
    inputsSchemas: (Record<string, BlazeSchema | undefined> | undefined)[],
    detailsSchemaId: string | undefined,
    formSchemaId: string | undefined,
    skipRuntimeVars?: (('account' | 'page' | 'user' | 'vars') | string)[],
  ): {
    columns: DatasetColumn[];
    options: EvalOption[];
  } {
    const options: EvalOption[] = [];
    const columns: DatasetColumn[] = [];
    if (!inputsSchemas?.length) {
      return { columns: [], options: [] };
    }

    const inputsNamesSchemasCache: Record<string, DatasetColumn[]> = {};
    for (const inputSchemas of inputsSchemas) {
      for (const inputName in inputSchemas) {
        // input1 is set as another named input. In order not to duplicate the options we skip it.
        // schemas that are duplicates of detailsSchema and formSchema should also be omitted
        if (
          inputName === MAIN_INPUT_NAME ||
          (skipRuntimeVars && skipRuntimeVars.indexOf(inputName) > -1) ||
          detailsSchemaId === inputSchemas[inputName]?.id ||
          formSchemaId === inputSchemas[inputName]?.id
        ) {
          continue;
        }
        const inputSchema = inputSchemas[inputName];
        const schemaColumns = inputSchema?.columns
          ? removeInternalNonIdColumns(inputSchema.columns)
          : [];
        if (inputsNamesSchemasCache[inputName]) {
          if (isDeepEqual(inputsNamesSchemasCache[inputName], schemaColumns)) {
            continue;
          } else {
            console.warn(`inputSchema name (${inputName}) duplicate with different schema`, {
              previous: inputsNamesSchemasCache[inputName],
              current: schemaColumns,
            });
          }
        }
        inputsNamesSchemasCache[inputName] = schemaColumns;
        for (const column of schemaColumns) {
          let label = `${inputName} | ${column.name}`;
          let value = `$$${inputName}["${getColumnKey(column)}"]`;
          const name = inputName;

          if (isNonObjectValueSchemaColumns(schemaColumns)) {
            // Non object values don't have properties, so we just use the input name as the label.
            label = inputName;
            value = `$$${inputName}`;
          } else if (inputName === 'row' || inputName === 'output') {
            // just the name of the column without the prefix.
            label = column.name;
          }
          options.push({ label, name, value });
          columns.push({ ...column, name: label });
        }
      }
    }

    return { columns, options };
  }

  /**
   * Creates stored variable options using variable definitions and variable infos.
   * Variable infos are used for backwards compatibility with old variables that don't have
   * variable definitions.
   */
  private createStoredVariablesOptions(): {
    storedVarsColumns: DatasetColumn[];
    storedVarsOptions: EvalOption[];
  } {
    const storedVarsOptions: EvalOption[] = [];
    const storedVarsColumns: DatasetColumn[] = [];
    const addedVarNames = new Set<string>();
    const varDefs = this.variablesFacade.varDefs();
    const varInfos = this.variablesFacade.varInfos();

    // Stored Variables
    const name = 'Stored Variable';
    const runtimeVarLabelPrevix = 'Stored Variable | ';
    for (const varDef of varDefs) {
      const label = `${runtimeVarLabelPrevix}${regexpGetStoredVariableName(varDef.name)}`;
      storedVarsOptions.push({ label, name, value: varDef.name });

      const column: DatasetColumn = {
        columnType: varDef.dataType,
        dataType: varDef.dataType,
        isProfilePicture: false,
        name: label,
      };
      storedVarsColumns.push(column);
      addedVarNames.add(varDef.name);
    }

    // Backwards compatibility for old variables. Add them as options even if they don't have
    // schema (variable definition) created for them yet.
    for (const varInfo of varInfos) {
      if (addedVarNames.has(varInfo.name)) {
        continue;
      }
      const label = `${runtimeVarLabelPrevix}${regexpGetStoredVariableName(varInfo.name)}`;
      storedVarsOptions.push({ label, name, value: varInfo.name });

      const column: DatasetColumn = {
        columnType: varInfo.column?.columnType,
        dataType: varInfo.column?.dataType,
        isProfilePicture: false,
        name: label,
      };
      storedVarsColumns.push(column);
      addedVarNames.add(varInfo.name);
    }

    return { storedVarsColumns, storedVarsOptions };
  }

  private createWizardSchemaOptions(wizardSchema: BlazeSchema | undefined): {
    columns: DatasetColumn[];
    options: EvalOption[];
  } {
    const options: EvalOption[] = [];
    let columns = wizardSchema?.columns ?? [];
    if (!wizardSchema?.columns?.length) {
      return { columns: [], options: [] };
    }

    columns = removeInternalNonIdColumns(columns);
    for (const column of columns) {
      options.push({
        label: `${wizardSchema.name} | ${column.name}`,
        name: wizardSchema.name,
        value: `$$${wizardSchema.inputName}["${column.refId}"]`,
      });
    }

    return { columns, options };
  }

  private disambiguateDuplicateOptions(
    columns: DatasetColumn[],
    options: EvalOption[],
  ): {
    columns: DatasetColumn[];
    options: EvalOption[];
  } {
    const updatedOptions: EvalOption[] = [];
    const parsedLabels = new Set<string>();
    const duplicateLabels = new Set<string>();
    const duplicateItemNames = new Set<string>();

    // Keep track of duplicate labels as well as opptions from the same schema which had at least one of the
    // items marked as duplicate. This is done to make sure that if one items displays a prefix, all other items
    // from that schema also display a prefix.
    for (const option of options) {
      if (parsedLabels.has(option.label)) {
        const parsedItem = options.find((opt) => opt.label === option.label);
        duplicateLabels.add(option.label);
        duplicateItemNames.add(option.name ?? '');
        duplicateItemNames.add(parsedItem?.name ?? '');
      }
      parsedLabels.add(option.label);
    }

    // Update labels of duplicate items
    for (const option of options) {
      if (
        option.name &&
        !option.label.startsWith(option.name) &&
        (duplicateLabels.has(option.label) || duplicateItemNames.has(option.name))
      ) {
        updatedOptions.push({ ...option, label: `${option.name} | ${option.label}` });
      } else {
        updatedOptions.push(option);
      }
    }

    return { options: updatedOptions, columns };
  }
}
